/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useReducer, ReactElement } from "react";

import dayjs from "dayjs";
import { Heading, Section } from "react-bulma-components";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { Navbar, PriceLabel, SystemNotification } from "components";
import { CountLabel } from "components/CountLabel/CountLabel";
import { ApplicationStatus } from "components/data-display/ApplicationStatus";
import { GlobalErrorScreen } from "components/GlobalErrorScreen";
import { DateButton } from "components/inputs/DateButton";
import { TransitsTable } from "components/TransitsTable/TransitsTable";
import {
  renderDestinations,
  renderWorkStatus,
  renderTotalAmountOfCommuting,
  renderTotalAmountOfTransportation,
} from "components/TravelExpensesTable/TravelExpensesTable";
import { showCommentInHTML } from "helpers/comment";
import { getDistanceKmBracketsString } from "helpers/distance";
import { convertHomeString } from "helpers/label";
import { RESPONSIVE } from "helpers/responsive";
import {
  useGetApproverUsersUserIdMovementLogApi,
  useGetApproverMovementRoutesApi,
} from "hooks/api";
import { MovementRoute } from "models/movementRoute";
import {
  filterZeroFareBySystem,
  TransportationExpense,
} from "models/transportationExpense";
import {
  MonthSummary,
  DaySummary,
  getMonthSummaryByResult,
  getDaySummariesByResult,
  CommutingExpense,
} from "models/travelExpense";
import { User } from "models/user";
import * as Table from "molecules/Table";

const MonthSummaryWrapper = styled.div`
  margin: 3rem 0 0;
  width: 100%;
  @media ${RESPONSIVE.DESKTOP} {
    width: 50%;
  }
`;

const MonthSummaryTh = styled(Table.Th)`
  width: 220px;
`;

interface ApplicantTravelExpensesState {
  monthSummary: MonthSummary;
  daySummaries: DaySummary[];
  isMovementLogsLoaded: boolean;
  expandedTravelExpenses: string[];
  applicantUser: User;
}

const initialState: ApplicantTravelExpensesState = {
  monthSummary: {
    movementLogApprovalId: undefined,
    applicationStatus: "",
    applicationStatusName: "",
    workCount: 0,
    totalDistanceKm: 0,
    totalFare: 0,
    totalTime: 0,
    applicantComment: undefined,
    approverComment: undefined,
    commuterPasses: [],
    commutingExpenses: {
      publicTransportationCount: 0,
      publicTransportationList: [],
      bicycleCount: 0,
      bicycleList: [],
      myCarCount: 0,
      myCarList: [],
      totalFare: 0,
    },
    travelExpenses: {
      publicTransportationCount: 0,
      publicTransportationList: [],
      totalFare: 0,
    },
  },
  daySummaries: [],
  isMovementLogsLoaded: false,
  expandedTravelExpenses: [],
  applicantUser: {} as User,
};

interface SetMovementLogsAction {
  type: "SET_MOVEMENT_LOGS";
  payload: {
    monthSummary: MonthSummary;
    daySummaries: DaySummary[];
  };
}

interface SetApplicantUserAction {
  type: "SET_APPLICANT_USER";
  payload: {
    applicantUser: User;
  };
}

interface SetIsMovementLogsLoadedAction {
  type: "SET_IS_MOVEMENT_LOGS_LOADED";
  payload: {
    isMovementLogsLoaded: boolean;
  };
}

interface ToggleTransitsAction {
  type: "TOGGLE_TRANSITS";
  payload: {
    id: string;
  };
}

interface SetMovementRoutesAction {
  type: "SET_MOVEMENT_ROUTES";
  payload: {
    results: {
      id: number;
      movementRoutes: MovementRoute[];
      isCommutingExpense: boolean;
      isTransportationExpense: boolean;
    }[];
  };
}

const setMovementRoutes = (
  state: ApplicantTravelExpensesState,
  action: SetMovementRoutesAction
) => {
  const { results } = action.payload;
  // eslint-disable-next-line no-restricted-syntax
  for (const result of results) {
    if (result.isCommutingExpense) {
      // eslint-disable-next-line no-restricted-syntax
      for (const daySummary of state.daySummaries) {
        // eslint-disable-next-line no-restricted-syntax
        for (const commutingExpense of daySummary.commutingExpenses) {
          if (commutingExpense.id === result.id) {
            commutingExpense.movementRoutes = result.movementRoutes;
          }
        }
      }
    }

    if (result.isTransportationExpense) {
      // eslint-disable-next-line no-restricted-syntax
      for (const daySummary of state.daySummaries) {
        filterZeroFareBySystem(result, daySummary.transportationExpenses);
      }
    }
  }
};

type TravelExpensesActions =
  | SetMovementLogsAction
  | SetIsMovementLogsLoadedAction
  | ToggleTransitsAction
  | SetMovementRoutesAction
  | SetApplicantUserAction;

const reducer: React.Reducer<
  ApplicantTravelExpensesState,
  TravelExpensesActions
> = (currentState, action) => {
  const newState: ApplicantTravelExpensesState = { ...currentState };

  switch (action.type) {
    case "SET_MOVEMENT_LOGS":
      newState.monthSummary = action.payload.monthSummary;
      newState.daySummaries = action.payload.daySummaries;
      break;

    case "SET_IS_MOVEMENT_LOGS_LOADED":
      newState.isMovementLogsLoaded = action.payload.isMovementLogsLoaded;
      break;
    case "TOGGLE_TRANSITS":
      // eslint-disable-next-line no-bitwise
      if (!~newState.expandedTravelExpenses.indexOf(action.payload.id)) {
        newState.expandedTravelExpenses.push(action.payload.id);
      } else {
        newState.expandedTravelExpenses.splice(
          newState.expandedTravelExpenses.indexOf(action.payload.id),
          1
        );
      }
      break;
    case "SET_MOVEMENT_ROUTES":
      setMovementRoutes(newState, action);
      break;
    case "SET_APPLICANT_USER":
      newState.applicantUser = action.payload.applicantUser;
      break;
    default:
      throw new Error();
  }
  return newState;
};

const getUserByAPIResponse = (user: any) => ({
  company: user.company,
  departmentEn: user.department_en,
  departmentJp: user.department_jp,
  employeeId: user.employee_id,
  id: user.id,
  nameEn: user.name_en,
  nameJp: user.name_jp,
});

const renderApproveMonth = (daySummaries: DaySummary[]) => {
  if (daySummaries.length === 0) {
    return "";
  }
  const approveMonth = dayjs(daySummaries[0].date)
    .locale("ja")
    .format("YYYY年MM月");
  return `申請月度：${approveMonth}度`;
};

const renderCommutingExpenseRoute = (commutingExpense: CommutingExpense) => {
  const arrivalName = commutingExpense.arrivalName
    ? commutingExpense.arrivalName
    : "自宅";
  const departureName = commutingExpense.departureName
    ? commutingExpense.departureName
    : "自宅";

  return (
    <div key={commutingExpense.id} className="cw-travel-expense-route">
      <div className="cw-travel-expense-route-meta">
        <div className="cw-travel-expense-route-meta-title">
          <h4 className="title is-4">{`${departureName} → ${arrivalName}`}</h4>
          <div className="cw-travel-expense-route-meta-title-item">
            {getDistanceKmBracketsString(commutingExpense.totalDistanceKm)}
          </div>
        </div>
      </div>
      <TransitsTable movementRoutes={commutingExpense.movementRoutes} />
    </div>
  );
};

const renderTransportationExpenseRoute = (
  transportationExpense: TransportationExpense
) => (
  <div key={transportationExpense.id} className="cw-travel-expense-route">
    <div className="cw-travel-expense-route-meta">
      <div className="cw-travel-expense-route-meta-title">
        <h4 className="title is-4">{`${transportationExpense.departureName} → ${transportationExpense.arrivalName}`}</h4>
      </div>
    </div>
    <TransitsTable movementRoutes={transportationExpense.movementRoutes} />
  </div>
);

const renderTravelExpense = (
  daySummary: DaySummary,
  expandedDaySummaries: string[],
  toggleTransits: (_daySummary: DaySummary) => void
) => {
  const visibility = expandedDaySummaries.find(
    (expandedDaySummary) => daySummary.date === expandedDaySummary
  )
    ? ""
    : "is-hidden";

  // eslint-disable-next-line no-param-reassign
  daySummary.commutingExpenses = daySummary.commutingExpenses.sort((a, _b) => {
    if (a.departureCode) return 1;
    return -1;
  });

  return (
    <div key={daySummary.date} className="cw-travel-expense">
      <div className="cw-travel-expense-header">
        <div className="cw-travel-expense-header-item">
          <DateButton
            daySummary={daySummary}
            onClick={() => toggleTransits(daySummary)}
          />
        </div>
        <div className="cw-travel-expense-header-item">
          {renderDestinations(daySummary)}
        </div>
        <div className="cw-travel-expense-header-item">
          {renderWorkStatus(daySummary)}
        </div>
        <div className="cw-travel-expense-header-item">
          <PriceLabel amount={renderTotalAmountOfCommuting(daySummary)} />
        </div>
        <div className="cw-travel-expense-header-item">
          <PriceLabel amount={renderTotalAmountOfTransportation(daySummary)} />
        </div>
        <div className="cw-travel-expense-header-item">
          <ApplicationStatus daySummary={daySummary} />
        </div>
      </div>
      <div className={`cw-travel-expense-routes ${visibility}`}>
        <div className="cw-travel-expense-route-meta">
          <h3 className="title is-3 cw-travel-expense-route-meta-title">
            移動区分: 通勤費
          </h3>
        </div>
        {daySummary.commutingExpenses.map((commutingExpense) =>
          renderCommutingExpenseRoute(commutingExpense)
        )}
        <div className="cw-travel-expense-route-transportation-meta">
          <h3 className="title is-3 cw-travel-expense-route-transportation-title">
            移動区分: 交通費
          </h3>
        </div>
        {daySummary.transportationExpenses.map((transportationExpense) =>
          renderTransportationExpenseRoute(transportationExpense)
        )}
      </div>
    </div>
  );
};

const fetchMovementRoute = async (
  userId: number,
  daySummary: DaySummary,
  getApi: ReturnType<typeof useGetApproverMovementRoutesApi>
) => {
  const promises = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const commutingExpense of daySummary.commutingExpenses) {
    promises.push({
      id: commutingExpense.id,
      promise: getApi(userId, commutingExpense.id),
      isCommutingExpense: true,
      isTransportationExpense: false,
    });
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const transportationExpense of daySummary.transportationExpenses) {
    promises.push({
      id: transportationExpense.id,
      promise: getApi(userId, transportationExpense.id),
      isCommutingExpense: false,
      isTransportationExpense: true,
    });
  }

  const results = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const movementRoute of promises) {
    // eslint-disable-next-line no-await-in-loop
    const result = await movementRoute.promise;

    results.push({
      id: movementRoute.id,
      movementRoutes: result.reduce(
        (acc: MovementRoute[], _movementRoute: any) => [
          ...acc,
          {
            arrivalId: _movementRoute.arrival_id,
            arrivalName: convertHomeString(_movementRoute.arrival_name),
            departureId: _movementRoute.departure_id,
            departureName: convertHomeString(_movementRoute.departure_name),
            distance: _movementRoute.distance,
            distanceKm: _movementRoute.distance_km,
            fare: _movementRoute.fare ? _movementRoute.fare : 0,
            id: _movementRoute.id,
            lineId: _movementRoute.line_id,
            lineName: _movementRoute.line_name,
            movementLogId: _movementRoute.movement_log_id,
            orderNo: _movementRoute.order_no,
            time: _movementRoute.time,
            transportationType: _movementRoute.transportation_type,
            transportationTypeName: _movementRoute.transportation_type_name,
            useCommuterPass: _movementRoute.use_commuter_pass,
          },
        ],
        []
      ),
      isCommutingExpense: movementRoute.isCommutingExpense,
      isTransportationExpense: movementRoute.isTransportationExpense,
    });
  }

  return results;
};

interface ApplicantExpensesProps {
  id?: string;
  userId?: string;
}

function ApplicantTravelExpensesContent(): ReactElement | null {
  const { id, userId } = useParams<ApplicantExpensesProps>();
  const [state, dispatch] = useReducer(reducer, initialState);

  const getApproverUsersUserIdMovementLogApi =
    useGetApproverUsersUserIdMovementLogApi();
  const getApproverMovementRoutesApi = useGetApproverMovementRoutesApi();

  useEffect(() => {
    (async () => {
      try {
        const data = await getApproverUsersUserIdMovementLogApi(userId!, id!);
        const monthSummary = getMonthSummaryByResult(data);
        const daySummaries = getDaySummariesByResult(data);
        const applicantUser = getUserByAPIResponse(data.user);

        dispatch({
          type: "SET_MOVEMENT_LOGS",
          payload: {
            monthSummary,
            daySummaries,
          },
        });

        dispatch({
          type: "SET_APPLICANT_USER",
          payload: {
            applicantUser,
          },
        });
      } finally {
        dispatch({
          type: "SET_IS_MOVEMENT_LOGS_LOADED",
          payload: {
            isMovementLogsLoaded: true,
          },
        });
      }
    })();
  }, [id, userId, getApproverUsersUserIdMovementLogApi]);

  return (
    <>
      <header>
        <Navbar />
      </header>
      <main role="main" className="container">
        <Section>
          <SystemNotification />
          <div>
            <Heading size={2}>申請された通勤費・交通費</Heading>
            <Heading size={6} subtitle>
              {renderApproveMonth(state.daySummaries)}
            </Heading>
          </div>
          {state.isMovementLogsLoaded && state.daySummaries.length > 0 && (
            <MonthSummaryWrapper>
              <Table.Table>
                <tbody>
                  <tr>
                    <MonthSummaryTh>申請ステータス</MonthSummaryTh>
                    <Table.Td>
                      {state.monthSummary.applicationStatusName}
                    </Table.Td>
                  </tr>
                </tbody>
              </Table.Table>
              <div>
                <Heading size={4}>申請情報</Heading>
              </div>
              <Table.Table>
                <tbody>
                  <tr>
                    <MonthSummaryTh>申請者名</MonthSummaryTh>
                    <Table.Td>
                      {state.applicantUser.nameJp} (
                      {state.applicantUser.employeeId})
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>出勤日数</MonthSummaryTh>
                    <Table.Td>{state.monthSummary.workCount} 日</Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>あなたのコメント</MonthSummaryTh>
                    <Table.Td>
                      {showCommentInHTML(state.monthSummary.approverComment!)}
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>申請者のコメント</MonthSummaryTh>
                    <Table.Td>
                      {showCommentInHTML(state.monthSummary.applicantComment!)}
                    </Table.Td>
                  </tr>
                </tbody>
              </Table.Table>
              <div>
                <Heading size={4}>通勤費</Heading>
              </div>
              <Table.Table>
                <tbody>
                  <tr>
                    <MonthSummaryTh>自転車利用回数</MonthSummaryTh>
                    <Table.Td>
                      <CountLabel
                        amount={
                          state.monthSummary.commutingExpenses.bicycleCount
                        }
                      />
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>マイカー利用回数</MonthSummaryTh>
                    <Table.Td>
                      <CountLabel
                        amount={state.monthSummary.commutingExpenses.myCarCount}
                      />
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>公共交通機関利用回数</MonthSummaryTh>
                    <Table.Td>
                      <CountLabel
                        amount={
                          state.monthSummary.commutingExpenses
                            .publicTransportationCount
                        }
                      />
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>公共交通機関利用交通費</MonthSummaryTh>
                    <Table.Td>
                      <PriceLabel
                        amount={state.monthSummary.commutingExpenses.totalFare}
                      />
                    </Table.Td>
                  </tr>
                </tbody>
              </Table.Table>
              <div>
                <i>※ 通勤費は給与振込時に、各種手当を加えた上で支給されます</i>
              </div>
              <div>
                <Heading size={4}>交通費</Heading>
              </div>
              <Table.Table>
                <tbody>
                  <tr>
                    <MonthSummaryTh>公共交通機関利用回数</MonthSummaryTh>
                    <Table.Td>
                      <CountLabel
                        amount={
                          state.monthSummary.travelExpenses
                            .publicTransportationCount
                        }
                      />
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>公共交通機関利用交通費</MonthSummaryTh>
                    <Table.Td>
                      <PriceLabel
                        amount={state.monthSummary.travelExpenses.totalFare}
                      />
                    </Table.Td>
                  </tr>
                </tbody>
              </Table.Table>
              <div>
                <i>※ 交通費は経費精算時に、他の経費と合わせて振込されます</i>
              </div>
            </MonthSummaryWrapper>
          )}
          <div className="cw-travel-expenses-wrapper">
            <div className="cw-travel-expenses">
              <div className="cw-travel-expenses-header">
                <div className="cw-travel-expenses-header-item">日付</div>
                <div className="cw-travel-expenses-header-item">
                  通勤・移動先
                </div>
                <div className="cw-travel-expenses-header-item">
                  勤務ステータス
                </div>
                <div className="cw-travel-expenses-header-item">通勤費</div>
                <div className="cw-travel-expenses-header-item">交通費</div>
                <div className="cw-travel-expenses-header-item">
                  通勤経路申請状況
                </div>
              </div>
              <div className="cw-travel-expenses-list">
                {state.daySummaries.map((daySummary) =>
                  renderTravelExpense(
                    daySummary,
                    state.expandedTravelExpenses,

                    async (_daySummary: DaySummary) => {
                      const results = await fetchMovementRoute(
                        state.applicantUser.id,
                        _daySummary,
                        getApproverMovementRoutesApi
                      );

                      dispatch({
                        type: "SET_MOVEMENT_ROUTES",
                        payload: {
                          results,
                        },
                      });

                      dispatch({
                        type: "TOGGLE_TRANSITS",
                        payload: {
                          id: _daySummary.date,
                        },
                      });
                    }
                  )
                )}
              </div>
            </div>
          </div>
        </Section>
      </main>
    </>
  );
}

export function ApplicantTravelExpenses(): ReactElement | null {
  return (
    <GlobalErrorScreen>
      <ApplicantTravelExpensesContent />
    </GlobalErrorScreen>
  );
}
/* eslint-enable */
